/**
 * AudioRemixShared -- Shared audio remix components for voice acting and video nodes.
 *
 * Exports:
 * - STATUS_INFO: Status badge config for dialogue alignment states
 * - BAR_COUNT: Number of waveform bars to render
 * - formatTime: Format seconds as M:SS.s
 * - useWaveform: Hook to decode audio and compute amplitude bars
 * - WaveformBars: Pure bar renderer (no player, no audio element)
 * - WaveformBar: Audio row with play button and waveform visualization
 */

import { memo, useEffect, useState } from 'react'
import { AudioWaveformVisualization } from './AudioWaveformVisualization'

/* -- Status badge config -- */

export const STATUS_INFO: Record<
  string,
  { className: string; tooltip: string }
> = {
  matched: {
    className: 'bg-green-500/20 text-green-400',
    tooltip:
      'Dialogue matched the transcribed audio at the expected time',
  },
  shifted: {
    className: 'bg-yellow-500/20 text-yellow-400',
    tooltip:
      'Dialogue found in audio but at a different timestamp than expected',
  },
  rephrased: {
    className: 'bg-orange-500/20 text-orange-400',
    tooltip:
      'Audio contained similar dialogue but with different wording',
  },
  dropped: {
    className: 'bg-red-500/20 text-red-400',
    tooltip:
      'Intended dialogue was not found in the generated video audio',
  },
  added: {
    className: 'bg-blue-500/20 text-blue-400',
    tooltip:
      'Line improvised by the video model — not in the original script',
  },
}

/* -- Utilities -- */

export function formatTime(s: number): string {
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${sec < 10 ? '0' : ''}${sec.toFixed(1)}`
}

/**
 * Parse a time string like "1:30" or "0:05" into seconds.
 * Returns fallback if the input is empty, non-numeric, or unparseable.
 */
export function parseTimeInput(str: string, fallback: number): number {
  const trimmed = str.trim()
  if (!trimmed) return fallback

  const parts = trimmed.split(':')
  if (parts.length === 2) {
    const mins = parseInt(parts[0], 10)
    const secs = parseFloat(parts[1])
    if (!isNaN(mins) && !isNaN(secs) && mins >= 0 && secs >= 0) {
      return mins * 60 + secs
    }
  } else if (parts.length === 1) {
    const secs = parseFloat(parts[0])
    if (!isNaN(secs) && secs >= 0) return secs
  }

  return fallback
}

/* -- Waveform hook: decode audio and compute amplitude bars -- */

export const BAR_COUNT = 48

// Cache decoded waveform bars by URL+count to survive re-renders and avoid re-decoding
const _waveformCache = new Map<string, number[]>()
// In-flight decodes to avoid duplicate work
const _inflight = new Map<string, Promise<number[]>>()

async function decodeWaveform(url: string, count: number): Promise<number[]> {
  const key = `${url}::${count}`

  // Return cached result
  const cached = _waveformCache.get(key)
  if (cached) return cached

  // Join in-flight decode for same URL
  const existing = _inflight.get(key)
  if (existing) return existing

  const promise = (async () => {
    try {
      const resp = await fetch(url)
      if (!resp.ok) return Array(count).fill(0.15) as number[]
      const buf = await resp.arrayBuffer()
      const ctx = new AudioContext()
      try {
        const audio = await ctx.decodeAudioData(buf)
        const raw = audio.getChannelData(0)
        const step = Math.max(1, Math.floor(raw.length / count))
        const result: number[] = []
        for (let i = 0; i < count; i++) {
          let sum = 0
          const start = i * step
          const end = Math.min(start + step, raw.length)
          for (let j = start; j < end; j++) sum += Math.abs(raw[j])
          result.push(sum / (end - start))
        }
        const max = Math.max(...result, 0.001)
        const bars = result.map((v) => v / max)
        _waveformCache.set(key, bars)
        return bars
      } finally {
        ctx.close()
      }
    } catch {
      // Generate pseudorandom fallback bars from URL hash so they look varied
      // instead of a uniform flat line
      const fallback: number[] = []
      let hash = 0
      for (let i = 0; i < url.length; i++) {
        hash = ((hash << 5) - hash + url.charCodeAt(i)) | 0
      }
      for (let i = 0; i < count; i++) {
        hash = (hash * 1103515245 + 12345) & 0x7fffffff
        fallback.push(0.15 + 0.7 * (hash / 0x7fffffff))
      }
      return fallback
    } finally {
      _inflight.delete(key)
    }
  })()

  _inflight.set(key, promise)
  return promise
}

export function useWaveform(url: string | null, count: number = BAR_COUNT): number[] {
  const [bars, setBars] = useState<number[]>(() => {
    if (!url) return []
    return _waveformCache.get(`${url}::${count}`) || []
  })

  useEffect(() => {
    if (!url) {
      setBars([])
      return
    }
    // Check cache synchronously first
    const cached = _waveformCache.get(`${url}::${count}`)
    if (cached) {
      setBars(cached)
      return
    }
    let cancelled = false
    decodeWaveform(url, count).then((result) => {
      if (!cancelled) setBars(result)
    })
    return () => { cancelled = true }
  }, [url, count])

  return bars
}

/* -- WaveformBars: pure bar renderer, no player or audio element -- */

interface WaveformBarsProps {
  /** Pre-computed amplitude bars (from useWaveform) */
  bars: number[]
  /** Number of bars to show in empty/loading state */
  barCount?: number
  /** Bar color */
  color?: string
  /** Opacity for filled bars (default 0.8) */
  opacity?: number
  /** Opacity for empty/loading bars (default 0.2) */
  emptyOpacity?: number
  /** Additional className for the container */
  className?: string
}

export const WaveformBars = memo(function WaveformBars({
  bars,
  barCount = 60,
  color = '#94a3b8',
  opacity = 0.8,
  emptyOpacity = 0.2,
  className,
}: WaveformBarsProps) {
  return (
    <div className={`flex items-center gap-[0.5px] ${className ?? ''}`}>
      {bars.length > 0
        ? bars.map((amp, i) => (
            <div
              key={i}
              className="flex-1 rounded-[0.5px]"
              style={{ height: `${Math.max(8, amp * 100)}%`, background: color, opacity }}
            />
          ))
        : Array.from({ length: barCount }, (_, i) => (
            <div
              key={i}
              className="flex-1 rounded-[0.5px]"
              style={{ height: '8%', background: color, opacity: emptyOpacity }}
            />
          ))}
    </div>
  )
})

WaveformBars.displayName = 'WaveformBars'

/* -- WaveformBar: audio row with label/radio on top, waveform player below -- */

interface WaveformBarProps {
  label: string
  url: string | null
  color: string
  selected?: boolean
  onSelect?: () => void
}

export const WaveformBar = memo(function WaveformBar({
  label,
  url,
  color,
  selected,
  onSelect,
}: WaveformBarProps) {
  return (
    <div>
      <div className="flex items-center gap-1 mb-1">
        {onSelect !== undefined && (
          <input
            type="radio"
            checked={!!selected}
            onChange={() => onSelect?.()}
            className="w-2.5 h-2.5 shrink-0 accent-primary cursor-pointer"
          />
        )}
        <span className="text-[7px] text-white/25">
          {label}
        </span>
      </div>
      <AudioWaveformVisualization
        url={url}
        height={24}
        barCount={48}
        accentColor={color}
        compact
      />
    </div>
  )
})

WaveformBar.displayName = 'WaveformBar'
